import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { manualRequestsService } from '../../services/myActionsService'
import { employeeService } from '../../services/employeeService'
import { ManAuthRequestResponse } from '../../types/ManAuthRequests'
import { manAuthUpdateRequest, DayHours } from '../../types/manAuthUpdateRequest'
import { calculateMaxAllowedHours, calculateUpdatedDays } from './hoursCalculations'
import { RequestStatus } from '../OccurrenceRequest/types'
import { RootStore, useAppDispatch } from '../../redux/store'
import { showErrorMessage } from '../../redux/reducers/snackbarReducer'
import { BaseResponse } from '../../types/base-response'
import UserErrorMessage from '../../utils/errorFilter'
import { useNonExistentRequestHandler } from '../OccurrenceRequest/utils'

export const useManAuthData = (id: string | undefined) => {
  const [manAuthRequest, setManAuthRequest] = useState<ManAuthRequestResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [comments, setComments] = useState('')
  const [approverComments, setApproverComments] = useState('')
  const [originalApproverComments, setOriginalApproverComments] = useState('')
  const [hours, setHours] = useState<number | null>(0)
  const [originalHours, setOriginalHours] = useState<number>(0)
  const [isHoursChanged, setIsHoursChanged] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [originalComments, setOriginalComments] = useState('')
  const [areFieldsChanged, setAreFieldsChanged] = useState(false)
  const [maxAllowedHours, setMaxAllowedHours] = useState<number>(0)
  const [defaultShiftLength, setDefaultShiftLength] = useState<number>(0)

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const activeRoute = useSelector<RootStore, string>(
    (state: RootStore) => state.appSettings.activeRoute
  )
  const handleNonExistentRequest = useNonExistentRequestHandler()

  const fetchManAuthRequestDetails = useCallback(async () => {
    if (!id) return

    setIsLoading(true)
    try {
      const response = await manualRequestsService.getManAuthRequestDetails(Number(id))
      setManAuthRequest(response)

      const maxHours = calculateMaxAllowedHours(response)
      setMaxAllowedHours(maxHours)

      if (response.totalHours !== undefined) {
        const currentTotalHours = response.totalHours
        setOriginalHours(currentTotalHours)
        setHours(currentTotalHours)
      } else {
        setOriginalHours(0)
        setHours(0)
      }

      if (response.employeeID) {
        try {
          const shiftLengthResponse = await employeeService.getEmployeeDefaultShiftLength(
            response.employeeID
          )
          setDefaultShiftLength(shiftLengthResponse.defaultShiftLength)
        } catch (error) {
          console.error('Error fetching default shift length:', error)
        }
      }

      if (response.reviewerComment) {
        setApproverComments(response.reviewerComment)
        setOriginalApproverComments(response.reviewerComment)
      } else {
        setApproverComments('')
        setOriginalApproverComments('')
      }

      if (response.requesterComment) {
        setComments(response.requesterComment)
        setOriginalComments(response.requesterComment)
      } else {
        setComments('')
        setOriginalComments('')
      }
    } catch (err: any) {
      setIsLoading(false)
      navigate(activeRoute || '/myactions')
      if (err.response.status === 404) {
        handleNonExistentRequest()
      }
      else if (err.response?.status === 403) {
        dispatch(showErrorMessage("You don't have permission to view this request."))
      } else {
        const response: BaseResponse = err.response?.data
        if (response?.errors) {
          response.errors.forEach(error => {
            dispatch(showErrorMessage(UserErrorMessage({ name: error.name })))
          })
        }
      }
      console.error('Error fetching ManAuth request details:', err)
    } finally {
      setIsLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchManAuthRequestDetails()
  }, [fetchManAuthRequestDetails])

  useEffect(() => {
    const hoursChanged = hours !== originalHours && hours !== null
    const commentsChanged = comments !== originalComments && originalComments !== ''
    const approverCommentsChanged =
      approverComments !== originalApproverComments && originalApproverComments !== ''

    setIsHoursChanged(hoursChanged)
    setAreFieldsChanged(hoursChanged || commentsChanged || approverCommentsChanged)
  }, [hours, originalHours, comments, originalComments, approverComments, originalApproverComments])

  const getStatusLabel = useCallback(
    (requestStatusId: number): string => {
      switch (requestStatusId) {
        case 1:
          return 'Approved'
        case 2:
          return 'Declined'
        case 3:
          return 'Amended'
        case 5:
          return 'Declined'
        case 6:
          return 'Pending'
        default:
          return manAuthRequest?.requestStatus || ''
      }
    },
    [manAuthRequest?.requestStatus]
  )

  const handleRequestUpdate = useCallback(
    async (requestStatusId: number) => {
      if (!manAuthRequest || !id) return

      setIsSubmitting(true)
      const newHours = hours ?? 0

      try {
        let updatedDays: DayHours[] = manAuthRequest.days.map(day => ({
          Date: day.date,
          Hours: day.hours,
        }))

        if (
          isHoursChanged &&
          (requestStatusId === RequestStatus.AMENDED || requestStatusId === RequestStatus.PENDING)
        ) {
          const hoursDifference = originalHours - newHours
          updatedDays = calculateUpdatedDays(hoursDifference, manAuthRequest, defaultShiftLength)
        }

        const updateRequest: manAuthUpdateRequest = {
          ID: Number(id),
          RequestStatusId: requestStatusId,
          ManagerComments: comments,
          ApproverComments: approverComments,
          Days: updatedDays,
        }

        await manualRequestsService.updateManAuthRequest(updateRequest)

        setManAuthRequest(prevState => {
          if (!prevState) return null
          return {
            ...prevState,
            requestStatus: getStatusLabel(requestStatusId),
            totalHours: newHours,
          }
        })

        setOriginalHours(newHours)
        setOriginalApproverComments(approverComments)
        setOriginalComments(comments)
        setAreFieldsChanged(false)
        setIsHoursChanged(false)
      } catch (error) {
        console.error('Error updating ManAuth request:', error)
      } finally {
        fetchManAuthRequestDetails().then(() => setIsSubmitting(false))
      }
    },
    [
      manAuthRequest,
      id,
      hours,
      maxAllowedHours,
      isHoursChanged,
      originalHours,
      defaultShiftLength,
      comments,
      approverComments,
      getStatusLabel,
      fetchManAuthRequestDetails,
    ]
  )

  const rows =
    manAuthRequest?.absenceDays.map((absDay, index) => ({
      id: absDay.absenceDayId || index,
      Date: absDay.absenceDate,
      absentHours: absDay.absentHours,
      OSP: absDay.sickHoursToBePaid,
      payApproval: absDay.payApproval,
      Unpaid: absDay.unpaid,
      requestStatus: absDay?.manAuthRequestStatus,
    })) || []

  const handleHoursChange = useCallback((newHours: number | null) => {
    setHours(newHours)
  }, [])

  const handleCommentsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setComments(e.target.value)
    },
    []
  )

  const handleApproverCommentsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setApproverComments(e.target.value)
    },
    []
  )

  const isRequestApproved = useCallback(
    () => manAuthRequest?.requestStatus === 'Approved',
    [manAuthRequest?.requestStatus]
  )

  return {
    manAuthRequest,
    isLoading,
    comments,
    setComments,
    approverComments,
    setApproverComments,
    hours,
    setHours,
    originalHours,
    isHoursChanged,
    isSubmitting,
    areFieldsChanged,
    maxAllowedHours,
    defaultShiftLength,
    rows,
    handleRequestUpdate,
    handleHoursChange,
    handleCommentsChange,
    handleApproverCommentsChange,
    isRequestApproved,
    navigate,
    fetchManAuthRequestDetails,
  }
}
